import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, input, inject, model, effect, forwardRef } from '@angular/core';
import { boolean } from 'mathjs';
import { PluggableMap, Feature } from 'ol';
import { Control } from 'ol/control';
import { Draw, Snap } from 'ol/interaction';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Subscription } from 'rxjs';
import { FenceDexie } from 'src/app/models/fence.model';
import { PropertyDexie } from 'src/app/models/property.model';
import { mapService } from 'src/app/services/map.service';
import { PropertyService } from 'src/app/services/property.service';
import { DrawType } from 'src/app/_helpers/enums';
import { CalculateLength, ConvertFeatureToGeoJson, ConvertFeatureToWKT } from 'src/app/_helpers/transformations';
import { ESKTOOL_TOKEN, EskTool } from 'src/app/shared/features/esk-tool/esk-tool-interface';
import { EskToolDirective } from 'src/app/shared/features/esk-tool/EskTool.directive';
import { CommonModule } from '@angular/common';

export interface FenceCreatedEvent{
  fence: FenceDexie,
  intersectedPaddockIds: Array<string>
}

@Component({
    selector: 'app-digitize-fence',
    templateUrl: './digitize-fence.component.html',
    styleUrls: ['./digitize-fence.component.css'],
    imports:[CommonModule],
    providers: [
        { provide: ESKTOOL_TOKEN, useExisting: forwardRef(() => DigitizeFenceComponent) }
    ],
    hostDirectives:[EskToolDirective]
})
export class DigitizeFenceComponent implements OnInit, OnDestroy, AfterViewInit, EskTool {
  private propertyService = inject(PropertyService);
  private elRef = inject(ElementRef);
  private mapService = inject(mapService);

  readonly map = input<PluggableMap>(undefined);
  readonly snapLayers = input<Array<VectorLayer<any>>>(undefined);

  @Output() fenceCreated = new EventEmitter<FenceCreatedEvent>();

  active = model<boolean>();
  disabled: boolean = false;
  activeProperty: PropertyDexie;
  interaction: Draw;
  snapInteractions: Array<Snap> = [];
  vectorSource: VectorSource<any>;
  control: Control;


  propertySub$: Subscription = new Subscription();

  activeEffect = effect(() => {
    const active = this.active();
    if (active) {
      this.draw();
    } else {
      this.deSelect();
    }
  });

  ngOnInit(): void {
    this.propertySub$ = this.propertyService.activeProperty.subscribe(property => {
      this.disabled = property == null ? true : false;
      this.activeProperty = property;
    });

    this.vectorSource = new VectorSource();
  }

  ngAfterViewInit(): void {
    this.control = new Control({target: this.elRef.nativeElement.parentNode, element: this.elRef.nativeElement});
    this.map().addControl(this.control);
  }


  ngOnDestroy(): void {
    if(this.control == null)
    return;

    this.propertySub$.unsubscribe();

    this.map().removeControl(this.control);
    this.deSelect();
  }


  toggleDigitizeFence(){
    this.active.set(!this.active());
  }


  private draw() {

    this.interaction = new Draw({
      source: this.vectorSource,
      type: DrawType.LineString,
      snapTolerance: 20,

    });

      this.snapLayers().forEach(lyr => {
        let snap = new Snap(
          {
            source: lyr.getSource()
          }
        )

        this.snapInteractions.push(
          snap
          )
      });

    this.map().addInteraction(this.interaction);

    this.snapInteractions.forEach(snap => {
      this.map().addInteraction(snap);
    })

    this.interaction.on('drawend', (event) => {
      var feature = event.feature as Feature<any>;

      this.createFence(feature);

    });

  }

  private async createFence(feature)
  {
    let fence = new FenceDexie();

    let length = await CalculateLength(feature);

    length = parseFloat(length.toFixed(2));

    fence.wkt = ConvertFeatureToWKT(feature);
    fence.length = length;
    fence.propertyID =this.activeProperty.propertyID;
    fence.wallabyProof = false;
    fence.existing = false;

   let intersectedPaddocks = await this.mapService.getIntersectedPaddocks(feature)

   this.fenceCreated.emit({fence:fence,intersectedPaddockIds:intersectedPaddocks})
  }

   deSelect() {

    this.map().removeInteraction(this.interaction);
    this.snapInteractions.forEach(snap => {
      this.map().removeInteraction(snap);
    });
    this.snapInteractions = [];
  }

}
